import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Regex pattern for validating numeric strings (defined at top level for performance)
const NUMERIC_PATTERN = /^[\s€.,\d-]+$/

/**
 * Format number in German format: 5.000,00
 * @param value - Number value (string or number)
 * @returns Formatted string in German format
 */
export function formatGermanNumber(value: string | number): string {
  if (!value && value !== 0) {
    return ""
  }

  const numValue = typeof value === "string" ? Number.parseFloat(value) : value

  if (Number.isNaN(numValue)) {
    return ""
  }

  // Convert to string with 2 decimal places
  const parts = numValue.toFixed(2).split(".")
  const integerPart = parts[0]
  const decimalPart = parts[1] || "00"

  // Add thousands separators (points)
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".")

  // Return German format: integer.decimal
  return `${formattedInteger},${decimalPart}`
}

/**
 * Parse German number format to internal format
 * Converts "5.000,00" to "5000.00"
 * @param value - German formatted number string
 * @returns Internal format number string
 */
export function parseGermanNumber(value: string): string {
  if (!value) {
    return ""
  }

  // Remove all spaces and € symbol
  let cleaned = value.replace(/\s+/g, "").replace(/€/g, "").trim()

  // Replace German format (point for thousands, comma for decimal)
  // with standard format (no thousands separator, point for decimal)
  cleaned = cleaned.replace(/\./g, "") // Remove thousands separators
  cleaned = cleaned.replace(/,/g, ".") // Replace comma with point for decimal

  // Validate it's a valid number
  const numValue = Number.parseFloat(cleaned)
  if (Number.isNaN(numValue)) {
    return ""
  }

  return numValue.toString()
}

/**
 * Check if a value can be parsed as a valid number
 * Handles both numeric values and string representations of numbers
 * @param value - Value to check (string, number, or object)
 * @returns True if the value can be parsed as a valid number
 */
export function isValidNumericValue(value: unknown): boolean {
  if (value === null || value === undefined || value === "") {
    return false
  }

  // If it's already a number, check if it's valid
  if (typeof value === "number") {
    return !Number.isNaN(value) && Number.isFinite(value)
  }

  // If it's an object, it's not numeric
  if (typeof value === "object") {
    return false
  }

  // Convert to string for parsing
  const stringValue = String(value).trim()

  if (stringValue === "") {
    return false
  }

  // Try to parse as German format first (remove spaces, €, and parse)
  let cleaned = stringValue.replace(/\s+/g, "").replace(/€/g, "").trim()
  cleaned = cleaned.replace(/\./g, "") // Remove thousands separators
  cleaned = cleaned.replace(/,/g, ".") // Replace comma with point

  // Try parsing as float
  const numValue = Number.parseFloat(cleaned)

  // Check if it's a valid number and the cleaned string represents the same value
  if (Number.isNaN(numValue) || !Number.isFinite(numValue)) {
    return false
  }

  // Additional check: if the original string contains non-numeric characters
  // (excluding spaces, €, dots, commas, and minus sign), it's not numeric
  if (!NUMERIC_PATTERN.test(stringValue)) {
    return false
  }

  return true
}
